/**
 * Iter 41.37 — Commercial Executive Dashboard (hm_admin only).
 *
 * Surfaces the single `/api/admin/commercial/executive-dashboard` endpoint:
 *   - leads/opps counts, pending approvals
 *   - total pipeline value + weighted forecast
 *   - per-advisor performance table
 *   - quick links to existing /admin/requests inbox for approvals
 *
 * Reuses the existing approval inbox at /admin/requests — does NOT
 * create a parallel approval surface.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp, Users, Building2, ClipboardList, AlertCircle, ArrowUpRight,
  Loader2, BadgeCheck, Activity, DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { getUiLocale } from '@/lib/dates';

const STAGES = [
  'lead', 'contacted', 'meeting_scheduled', 'proposal_sent',
  'negotiation', 'won', 'lost',
];

const fmtMoney = (n) => n == null ? '—' :
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const fmtNum = (n) => n == null ? '0' : new Intl.NumberFormat().format(n);

export default function AdminCommercialDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await adminApi.commercialExecutiveDashboard();
        if (alive) setData(d);
      } catch (e) {
        toast.error(t('pages.commercialDashboard.loadFailed', {
          defaultValue: 'Failed to load dashboard',
        }), { description: formatApiError(e) });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [t]);

  const totals = useMemo(() => {
    if (!data) return null;
    const s = data.by_status || {};
    const sumOf = (codes) =>
      codes.reduce((acc, c) => acc + ((s[c]?.count) || 0), 0);
    return {
      new_leads:    s.lead?.count || 0,
      active_opps:  sumOf(['contacted', 'meeting_scheduled', 'proposal_sent', 'negotiation']),
      won_opps:     s.won?.count || 0,
      lost_opps:    s.lost?.count || 0,
      total_value:  data.totals?.pipeline_value || 0,
      closed_won:   data.totals?.closed_won_value || 0,
      weighted:     data.totals?.weighted_forecast || 0,
    };
  }, [data]);

  // RBAC backstop: route guard already restricts; double-check anyway.
  if (user && user.role !== 'hm_admin') {
    return <Navigate to="/admin" replace />;
  }

  if (loading) {
    return (
      <>
        <Topbar
          title={t('pages.commercialDashboard.title', { defaultValue: 'Commercial Executive Dashboard' })}
        />
        <div className="p-6 flex items-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
        </div>
      </>
    );
  }
  if (!data) return null;

  return (
    <>
      <Topbar
        title={t('pages.commercialDashboard.title', { defaultValue: 'Commercial Executive Dashboard' })}
        subtitle={t('pages.commercialDashboard.subtitle', {
          defaultValue: 'Pipeline, forecast and advisor performance at a glance.',
        })}
      />
      <div className="p-6 lg:p-8 space-y-6" data-testid="commercial-dashboard">

        {/* Headline KPI tiles */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ced-kpis">
          <KpiTile
            icon={TrendingUp} testid="ced-kpi-new"
            label={t('pages.commercialDashboard.kpi.new', { defaultValue: 'New leads' })}
            value={fmtNum(totals.new_leads)}
          />
          <KpiTile
            icon={Activity} testid="ced-kpi-active"
            label={t('pages.commercialDashboard.kpi.active', { defaultValue: 'Active opportunities' })}
            value={fmtNum(totals.active_opps)}
          />
          <KpiTile
            icon={BadgeCheck} testid="ced-kpi-won" tone="success"
            label={t('pages.commercialDashboard.kpi.won', { defaultValue: 'Won' })}
            value={fmtNum(totals.won_opps)}
          />
          <KpiTile
            icon={AlertCircle} testid="ced-kpi-lost" tone="muted"
            label={t('pages.commercialDashboard.kpi.lost', { defaultValue: 'Lost' })}
            value={fmtNum(totals.lost_opps)}
          />
        </div>

        {/* Pipeline value row — Founder UAT (Finding I): open pipeline
            excludes Won/Lost; Won reported separately as closed-won. */}
        <div className="grid sm:grid-cols-3 gap-4">
          <Card className="p-5 shadow-card" data-testid="ced-total-pipeline">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
              {t('pages.commercialDashboard.totalPipeline', { defaultValue: 'Open pipeline value' })}
            </p>
            <p className="font-display text-3xl font-bold">{fmtMoney(totals.total_value)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('pages.commercialDashboard.totalPipelineHelp', {
                defaultValue: 'Sum of estimated value across open stages (excludes Won and Lost).',
              })}
            </p>
          </Card>
          <Card className="p-5 shadow-card" data-testid="ced-closed-won">
            <p className="text-xs uppercase tracking-wider font-semibold text-success mb-1">
              {t('pages.commercialDashboard.closedWon', { defaultValue: 'Closed-won value' })}
            </p>
            <p className="font-display text-3xl font-bold text-success">{fmtMoney(totals.closed_won)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('pages.commercialDashboard.closedWonHelp', {
                defaultValue: 'Estimated value of Won opportunities.',
              })}
            </p>
          </Card>
          <Card className="p-5 shadow-card bg-primary/5 border-primary/30" data-testid="ced-weighted-forecast">
            <p className="text-xs uppercase tracking-wider font-semibold text-primary mb-1">
              {t('pages.commercialDashboard.weightedForecast', { defaultValue: 'Weighted forecast' })}
            </p>
            <p className="font-display text-3xl font-bold text-primary">{fmtMoney(totals.weighted)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('pages.commercialDashboard.weightedHelp', {
                defaultValue: 'Each open opportunity weighted by stage probability (10/25/40/60/80%).',
              })}
            </p>
          </Card>
        </div>

        {/* Iter 41.38 — Billing KPIs (additive) */}
        {data.billing && (
          <Card className="p-5 shadow-card" data-testid="ced-billing">
            <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              {t('pages.commercialDashboard.billingTitle', { defaultValue: 'Billing & Subscriptions' })}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <CurrencyKpi
                testid="ced-mrr"
                label={t('pages.commercialDashboard.mrr', { defaultValue: 'Monthly Recurring Revenue (MRR)' })}
                amounts={data.billing.mrr_by_currency}
              />
              <CurrencyKpi
                testid="ced-acv"
                label={t('pages.commercialDashboard.acv', { defaultValue: 'Annual Contract Value (ACV)' })}
                amounts={data.billing.acv_by_currency}
              />
              <CurrencyKpi
                testid="ced-outstanding"
                tone="warning"
                label={t('pages.commercialDashboard.outstanding', { defaultValue: 'Outstanding balances' })}
                amounts={data.billing.outstanding_by_currency}
              />
              <SimpleKpi
                testid="ced-renew30"
                label={t('pages.commercialDashboard.renew30', { defaultValue: 'Upcoming renewals (30d)' })}
                value={data.billing.upcoming_renewals_30d ?? 0}
              />
              <SimpleKpi
                testid="ced-renew15"
                tone="warning"
                label={t('pages.commercialDashboard.renew15', { defaultValue: 'Upcoming renewals (15d)' })}
                value={data.billing.upcoming_renewals_15d ?? 0}
              />
              <SimpleKpi
                testid="ced-expired"
                tone="destructive"
                label={t('pages.commercialDashboard.expiredSubs', { defaultValue: 'Expired subscriptions' })}
                value={data.billing.expired_subscriptions ?? 0}
              />
              <SimpleKpi
                testid="ced-upsell"
                label={t('pages.commercialDashboard.upsellOpps', { defaultValue: 'Upsell opportunities' })}
                value={data.billing.upsell_opportunities ?? 0}
              />
            </div>
          </Card>
        )}

        {/* Pending approvals */}
        <Card className="p-5 shadow-card" data-testid="ced-pending">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-display text-lg font-bold flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-primary" />
                {t('pages.commercialDashboard.pendingTitle', { defaultValue: 'Pending approvals' })}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {t('pages.commercialDashboard.pendingHelp', {
                  defaultValue: 'Review and decide in the unified Requests inbox.',
                })}
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/admin/leads')}
                    data-testid="ced-open-inbox">
              <ArrowUpRight className="h-4 w-4 mr-1" />
              {t('pages.commercialDashboard.openInbox', { defaultValue: 'Open Requests inbox' })}
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <PendingTile
              testid="ced-pending-client"
              icon={Building2}
              label={t('pages.commercialDashboard.pendingClient', {
                defaultValue: 'Client creation requests',
              })}
              count={data.pending_requests?.client_creation || 0}
              onClick={() => navigate('/admin/leads')}
            />
            <PendingTile
              testid="ced-pending-suite"
              icon={Users}
              label={t('pages.commercialDashboard.pendingSuite', {
                defaultValue: 'Suite activation requests',
              })}
              count={data.pending_requests?.suite_activation || 0}
              onClick={() => navigate('/admin/leads')}
            />
          </div>
        </Card>

        {/* Pipeline by stage */}
        <Card className="p-5 shadow-card" data-testid="ced-by-stage">
          <h3 className="font-display text-lg font-bold mb-3">
            {t('pages.commercialDashboard.byStage', { defaultValue: 'Pipeline by stage' })}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="py-2">{t('pages.commercialDashboard.stage', { defaultValue: 'Stage' })}</th>
                    <th className="py-2 text-right">{t('pages.commercialDashboard.leads', { defaultValue: 'Leads' })}</th>
                    <th className="py-2 text-right">{t('pages.commercialDashboard.pipelineValue', { defaultValue: 'Pipeline value' })}</th>
                    <th className="py-2 text-right">{t('pages.commercialDashboard.weight', { defaultValue: 'Weight' })}</th>
                    <th className="py-2 text-right">{t('pages.commercialDashboard.weightedValue', { defaultValue: 'Weighted value' })}</th>
                </tr>
              </thead>
              <tbody>
                {STAGES.map((s) => {
                  const row = (data.by_status || {})[s] || { count: 0, value_total: 0 };
                  const weight = data.weights?.[s] ?? 0;
                  return (
                    <tr key={s} className="border-t" data-testid={`ced-stage-row-${s}`}>
                      <td className="py-2"><Badge variant="outline">{t(`pages.crm.status.${s}`)}</Badge></td>
                      <td className="py-2 text-right">{fmtNum(row.count)}</td>
                      <td className="py-2 text-right">{fmtMoney(row.value_total)}</td>
                      <td className="py-2 text-right text-muted-foreground">{Math.round(weight * 100)}%</td>
                      <td className="py-2 text-right font-medium">{fmtMoney(row.value_total * weight)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Per-advisor performance */}
        <Card className="p-5 shadow-card" data-testid="ced-advisors">
          <h3 className="font-display text-lg font-bold mb-3">
            {t('pages.commercialDashboard.advisorPerf', { defaultValue: 'Advisor performance' })}
          </h3>
          {(data.per_advisor || []).length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              {t('pages.commercialDashboard.noAdvisors', { defaultValue: 'No advisors registered yet.' })}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="py-2">{t('pages.commercialDashboard.advisor', { defaultValue: 'Advisor' })}</th>
                    <th className="py-2 text-right">{t('pages.commercialDashboard.leads', { defaultValue: 'Leads' })}</th>
                    <th className="py-2 text-right">{t('pages.commercialDashboard.clients', { defaultValue: 'Clients' })}</th>
                    <th className="py-2 text-right">{t('pages.commercialDashboard.openOpps', { defaultValue: 'Open opps' })}</th>
                    <th className="py-2 text-right">{t('pages.commercialDashboard.wonOpps', { defaultValue: 'Won opps' })}</th>
                    <th className="py-2 text-right">{t('pages.commercialDashboard.pipelineValue', { defaultValue: 'Pipeline value' })}</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data.per_advisor || []).map((row) => (
                    <tr key={row.advisor_id} className="border-t hover:bg-muted/30"
                        data-testid={`ced-advisor-row-${row.advisor_id}`}>
                      <td className="py-2 font-medium">{row.name}</td>
                      <td className="py-2 text-right">{fmtNum(row.leads)}</td>
                      <td className="py-2 text-right">{fmtNum(row.clients)}</td>
                      <td className="py-2 text-right">{fmtNum(row.open_opps)}</td>
                      <td className="py-2 text-right">{fmtNum(row.won_opps)}</td>
                      <td className="py-2 text-right">{fmtMoney(row.pipeline_value)}</td>
                      <td className="py-2 text-right">
                        <Button variant="ghost" size="sm"
                                onClick={() => navigate(`/admin/crm?advisor=${row.advisor_id}`)}
                                data-testid={`ced-advisor-open-${row.advisor_id}`}>
                          {t('pages.commercialDashboard.openAdvisor', { defaultValue: 'View pipeline' })}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

function KpiTile({ icon: Icon, label, value, tone, testid }) {
  const toneClass = tone === 'success' ? 'text-success' :
                    tone === 'muted'   ? 'text-muted-foreground' : 'text-primary';
  return (
    <Card className="p-4 shadow-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
        <Icon className={`h-4 w-4 ${toneClass}`} />
      </div>
      <p className={`font-display text-3xl font-bold mt-1 ${toneClass}`}>{value}</p>
    </Card>
  );
}

function PendingTile({ icon: Icon, label, count, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-left p-4 border rounded-md hover:bg-muted/30 transition-colors flex items-center gap-3"
    >
      <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm">{label}</p>
        <p className="font-display text-2xl font-bold">{count}</p>
      </div>
      <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}

// Iter 41.38 — KPI helpers used by the billing block (multi-currency aware)
function CurrencyKpi({ label, amounts, tone, testid }) {
  const fmt = (n, c) => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: c || 'USD', maximumFractionDigits: 0 }).format(Number(n || 0));
    } catch { return `${c} ${Number(n || 0).toLocaleString(getUiLocale())}`; }
  };
  const entries = Object.entries(amounts || {});
  const toneClass = tone === 'warning' ? 'text-warning' : 'text-foreground';
  return (
    <div className="p-3 border rounded-md" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
      {entries.length === 0 ? (
        <p className={`font-display text-xl font-bold mt-1 ${toneClass}`}>—</p>
      ) : (
        <ul className="mt-1 space-y-0.5">
          {entries.map(([c, v]) => (
            <li key={c} className={`font-medium text-sm ${toneClass}`}>{fmt(v, c)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SimpleKpi({ label, value, tone, testid }) {
  const toneClass = tone === 'warning' ? 'text-warning' : tone === 'destructive' ? 'text-destructive' : 'text-foreground';
  return (
    <div className="p-3 border rounded-md" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
      <p className={`font-display text-2xl font-bold mt-1 ${toneClass}`}>{value}</p>
    </div>
  );
}
